import { lazy, Suspense, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarPlus, Circle, LogIn, Pencil, Radio, Trash2 } from 'lucide-react';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { BarreSites } from '../components/ui/BarreSites';
import { useToast } from '../components/ui/useToast';
import { CarteReplays } from '../components/elearning/CarteReplays';
import { CarteEmargementClasse } from '../components/elearning/CarteEmargementClasse';
import { LecteurReplay } from '../components/elearning/LecteurReplay';
import { ModaleClasse } from '../components/elearning/ModaleClasse';
import { PlanFormation } from '../components/elearning/PlanFormation';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import {
  LIBELLES_DEMI_JOURNEE,
  dateCourte,
  jetonAnimateur,
  urlVideoReplay,
  useGestesClasse,
  useSalleElearning,
  type DetailClasse,
  type LigneClasse,
  type LigneReplay,
} from '../lib/useElearning';
import { attributsOnglet, attributsPanneau, clavierOnglets } from '../components/ui/onglets-aria';

// La salle LiveKit n'est chargée qu'à l'entrée en direct : elle pèse plus que
// tout le reste de l'écran.
const SalleDirect = lazy(() => import('../components/elearning/SalleDirect'));

type Onglet = 'A_VENIR' | 'PASSEES';

function message(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function LigneListeClasse({
  classe,
  choisie,
  onChoisir,
}: {
  classe: LigneClasse;
  choisie: boolean;
  onChoisir: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onChoisir}
      aria-current={choisie ? 'true' : undefined}
      className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left transition sm:flex-nowrap sm:px-6 ${
        choisie ? 'bg-codialis-sky/10' : 'hover:bg-gray-50'
      }`}
    >
      <div className="w-24 shrink-0 font-mono text-xs text-codialis-gray">
        {dateCourte(classe.date)}
        <span className="block text-[10px]">
          {classe.heureDebut}–{classe.heureFin}
        </span>
      </div>
      <div className="min-w-0 flex-1 basis-40">
        <div className="truncate text-sm font-semibold text-codialis-navy">{classe.libelle}</div>
        <div className="truncate text-[11px] text-codialis-gray">
          {classe.promotion.libelle}
          {classe.formateur ? ` · ${classe.formateur}` : ' · sans formateur'}
          {classe.replays > 0 ? ` · ${classe.replays} replay${classe.replays > 1 ? 's' : ''}` : ''}
        </div>
      </div>
      <PastilleSite site={classe.siteCode} />
      <Pastille texte={classe.etatLibelle} ton={classe.etatTon} />
    </button>
  );
}

/**
 * « Salle e-learning » (CDC §7.2) : programmer les classes à distance, les
 * diffuser en direct dans la salle LiveKit, les enregistrer, en archiver les
 * replays, et les ranger dans le plan de formation.
 *
 * États, refus et droits viennent de l'API : l'écran ne recalcule rien.
 */
export function FormElearningPage(): JSX.Element {
  const { site } = usePerimetre();
  const toast = useToast();
  // `?seance=` : la classe choisie depuis le tableau de bord du formateur (TF-456).
  const [parametres] = useSearchParams();
  const [seanceId, setSeanceId] = useState<string | null>(() => parametres.get('seance'));
  const { data, isLoading, error } = useSalleElearning(site, seanceId);
  const gestes = useGestesClasse();
  const [enSalle, setEnSalle] = useState(false);
  const [programmation, setProgrammation] = useState(false);
  const [edition, setEdition] = useState(false);
  const [confirmation, setConfirmation] = useState<'FERMER' | 'RETIRER' | null>(null);
  const [lecture, setLecture] = useState<LigneReplay | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [onglet, setOnglet] = useState<Onglet>('A_VENIR');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  const selection: DetailClasse | null = data?.selection ?? null;
  const classes = useMemo(
    () =>
      (data?.classes ?? []).filter((classe) =>
        onglet === 'A_VENIR'
          ? classe.etat === 'PROGRAMMEE' || classe.etat === 'EN_DIRECT'
          : classe.etat === 'TERMINEE' || classe.etat === 'NON_TENUE',
      ),
    [data, onglet],
  );
  const pages = Math.max(1, Math.ceil(classes.length / taillePage));
  const pageCourante = Math.min(page, pages);
  const visibles = classes.slice((pageCourante - 1) * taillePage, pageCourante * taillePage);

  const geste = async (action: () => Promise<unknown>, succes: string, defaut: string): Promise<boolean> => {
    setErreurGeste(null);
    try {
      await action();
      toast(succes);
      return true;
    } catch (cause) {
      setErreurGeste(message(cause, defaut));
      // En salle, l'en-tête de l'écran est masqué : le message passe en bulle.
      if (enSalle) toast(message(cause, defaut));
      return false;
    }
  };

  const demarrer = async () => {
    if (!selection) return;
    const ouverte = await geste(
      () => gestes.ouvrir.mutateAsync(selection.seanceId),
      'Diffusion en direct démarrée · les apprenants sont prévenus',
      'La diffusion n’a pas pu démarrer.',
    );
    if (ouverte) setEnSalle(true);
  };

  const fermer = async () => {
    if (!selection) return;
    await geste(
      () => gestes.fermer.mutateAsync(selection.seanceId),
      'Diffusion fermée · la salle est vidée',
      'La diffusion n’a pas pu être fermée.',
    );
    setConfirmation(null);
  };

  const retirer = async () => {
    if (!selection) return;
    const fait = await geste(
      () => gestes.retirer.mutateAsync(selection.seanceId),
      'Classe retirée',
      'La classe n’a pas pu être retirée.',
    );
    setConfirmation(null);
    if (fait) setSeanceId(null);
  };

  const enDirect = selection?.etat === 'EN_DIRECT';
  const occupe = gestes.ouvrir.isPending || gestes.fermer.isPending;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Salle e-learning"
        sousTitre="Espace formateur · diffusion en streaming, replay archivé, caméra obligatoire"
        actions={
          <>
            <Link
              to="/emargement"
              className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Feuille d’émargement
            </Link>
            {data && !data.refusProgrammation && (
              <BoutonPrincipal onClick={() => setProgrammation(true)}>
                <CalendarPlus className="mr-2 h-4 w-4" aria-hidden />
                Programmer une classe
              </BoutonPrincipal>
            )}
          </>
        }
      />

      <BarreSites />

      {data?.diffusion.motif && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 sm:px-5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
          <p className="text-sm text-amber-900">{data.diffusion.motif}</p>
        </div>
      )}
      {data?.refusProgrammation && data.estFormateur && (
        <p className="rounded-2xl bg-white px-4 py-3 text-sm text-codialis-gray shadow-card sm:px-5">
          {data.refusProgrammation}
        </p>
      )}

      {error && <MessageErreur message={message(error, 'La salle e-learning n’a pas pu être chargée.')} />}
      {isLoading && <Chargement libelle="Chargement de la salle…" />}

      {data && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="min-w-0 space-y-6 lg:col-span-2">
            {/* Classe sélectionnée : l'espace du direct. */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-codialis-navy via-codialis-deep to-codialis-blue p-5 text-white shadow-card sm:p-8">
              {selection ? (
                <>
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    {enDirect ? (
                      <span className="flex items-center gap-2 rounded-lg bg-red-600 px-2.5 py-1 text-xs font-bold">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-white" aria-hidden />
                        EN DIRECT
                      </span>
                    ) : (
                      <Pastille texte={selection.etatLibelle} ton={selection.etatTon} />
                    )}
                    {selection.enregistrementEnCours && (
                      <span className="flex items-center gap-1.5 rounded-lg bg-black/40 px-2.5 py-1 text-xs font-semibold">
                        <Circle className="h-3 w-3 fill-red-500 text-red-500" aria-hidden />
                        Enregistrement · replay archivé
                      </span>
                    )}
                    <PastilleSite site={selection.siteCode} />
                  </div>
                  <h2 className="font-display text-xl font-bold sm:text-2xl">{selection.libelle}</h2>
                  <p className="mt-1 text-sm text-white/70">
                    {selection.promotion.libelle}
                    {!selection.bloc
                      ? ' · hors programme'
                      : selection.bloc.libelle !== selection.libelle
                        ? ` · ${selection.bloc.libelle}`
                        : ''}
                  </p>
                  <p className="mt-1 text-sm text-white/70">
                    {dateCourte(selection.date)} · {LIBELLES_DEMI_JOURNEE[selection.demiJournee]} ·{' '}
                    {selection.heureDebut}–{selection.heureFin} · {selection.formateur ?? 'sans formateur'}
                  </p>
                  <p className="mt-1 text-xs text-white/60">
                    {selection.emarges}/{selection.attendus} apprenant{selection.attendus > 1 ? 's' : ''} émargé
                    {selection.emarges > 1 ? 's' : ''}
                    {selection.diffusion.ouvertePar ? ` · diffusion ouverte par ${selection.diffusion.ouvertePar}` : ''}
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {enDirect && selection.refus.rejoindre === null && (
                      <button
                        type="button"
                        onClick={() => setEnSalle(true)}
                        className="gradient-mint inline-flex min-h-[44px] items-center gap-2 rounded-xl px-4 text-sm font-semibold text-codialis-navy"
                      >
                        <LogIn className="h-4 w-4" aria-hidden />
                        Rejoindre la salle
                      </button>
                    )}
                    {!enDirect && selection.refus.ouverture === null && (
                      <button
                        type="button"
                        disabled={occupe}
                        onClick={() => void demarrer()}
                        className="gradient-mint inline-flex min-h-[44px] items-center gap-2 rounded-xl px-4 text-sm font-semibold text-codialis-navy disabled:opacity-60"
                      >
                        <Radio className="h-4 w-4" aria-hidden />
                        Démarrer la diffusion
                      </button>
                    )}
                    {enDirect && selection.refus.fermeture === null && (
                      <button
                        type="button"
                        disabled={occupe}
                        onClick={() => setConfirmation('FERMER')}
                        className="inline-flex min-h-[44px] items-center rounded-xl border border-white/30 px-4 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                      >
                        Fermer la diffusion
                      </button>
                    )}
                    {selection.estAnimateur && (
                      <button
                        type="button"
                        onClick={() => setEdition(true)}
                        className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-white/30 px-4 text-sm font-semibold text-white hover:bg-white/10"
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                        Modifier
                      </button>
                    )}
                    {selection.refus.retrait === null && (
                      <button
                        type="button"
                        onClick={() => setConfirmation('RETIRER')}
                        className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-white/30 px-4 text-sm font-semibold text-white hover:bg-white/10"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                        Retirer
                      </button>
                    )}
                  </div>
                  {!enDirect && selection.refus.ouverture && selection.estAnimateur && (
                    <p className="mt-3 text-xs text-white/60">{selection.refus.ouverture}</p>
                  )}
                  {erreurGeste && (
                    <p role="alert" className="mt-3 rounded-lg bg-red-600/90 px-3 py-2 text-xs font-semibold">
                      {erreurGeste}
                    </p>
                  )}
                </>
              ) : (
                <div className="py-6 text-center">
                  <h2 className="font-display text-lg font-bold">Aucune classe à distance à venir</h2>
                  <p className="mt-1 text-sm text-white/70">
                    {data.refusProgrammation
                      ? 'Les classes programmées pour votre antenne apparaîtront ici.'
                      : 'Programmez une classe : les apprenants de la promotion sont prévenus et la rejoignent le jour venu.'}
                  </p>
                </div>
              )}
            </div>

            {selection && <CarteEmargementClasse classe={selection} />}

            <Carte
              titre="Classes à distance"
              actions={
                <div
                  role="tablist"
                  aria-label="Période"
                  onKeyDown={clavierOnglets(['A_VENIR', 'PASSEES'] as const, onglet, (valeur) => {
                    setOnglet(valeur);
                    setPage(1);
                  })}
                  className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-0.5"
                >
                  {(
                    [
                      ['A_VENIR', 'À venir'],
                      ['PASSEES', 'Passées'],
                    ] as const
                  ).map(([valeur, libelle]) => (
                    <button
                      key={valeur}
                      type="button"
                      {...attributsOnglet(valeur, onglet === valeur, 'panneau-classes-distance')}
                      onClick={() => {
                        setOnglet(valeur);
                        setPage(1);
                      }}
                      className={`min-h-[36px] rounded-lg px-3 text-xs font-semibold [@media(pointer:coarse)]:min-h-[40px] ${
                        onglet === valeur ? 'bg-white text-codialis-navy shadow-xs' : 'text-codialis-gray'
                      }`}
                    >
                      {libelle}
                    </button>
                  ))}
                </div>
              }
            >
              <div {...attributsPanneau('panneau-classes-distance', onglet)} className="divide-y divide-gray-100">
                {visibles.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">
                    {onglet === 'A_VENIR'
                      ? 'Aucune classe à distance programmée.'
                      : 'Aucune classe à distance ces quatre derniers mois.'}
                  </p>
                ) : (
                  visibles.map((classe) => (
                    <LigneListeClasse
                      key={classe.seanceId}
                      classe={classe}
                      choisie={classe.seanceId === selection?.seanceId}
                      onChoisir={() => {
                        setSeanceId(classe.seanceId);
                        setErreurGeste(null);
                      }}
                    />
                  ))
                )}
                {classes.length > 0 && (
                  <Pagination
                    page={pageCourante}
                    pages={pages}
                    total={classes.length}
                    affiches={visibles.length}
                    taillePage={taillePage}
                    onPage={setPage}
                    onChangerTaille={(taille) => {
                      setTaillePage(taille);
                      setPage(1);
                    }}
                    uniteLabel="classe"
                  />
                )}
              </div>
            </Carte>
          </div>

          <div className="min-w-0 space-y-6">
            {selection && <CarteReplays classe={selection} onLire={setLecture} />}
            {selection && (
              <Carte titre="Plan de formation associé">
                <PlanFormation plan={selection.plan} />
              </Carte>
            )}
          </div>
        </div>
      )}

      {data && (
        <ModaleClasse
          ouverte={programmation}
          onFermer={() => setProgrammation(false)}
          promotions={data.promotions}
          formateurs={data.formateurs}
          onProgrammee={setSeanceId}
        />
      )}
      {data && selection && (
        <ModaleClasse
          ouverte={edition}
          onFermer={() => setEdition(false)}
          promotions={data.promotions}
          formateurs={data.formateurs}
          classe={selection}
        />
      )}

      <Modale
        titre={confirmation === 'FERMER' ? 'Fermer la diffusion ?' : 'Retirer cette classe ?'}
        sousTitre={
          confirmation === 'FERMER'
            ? 'Les apprenants quittent la salle et l’enregistrement en cours s’arrête. La diffusion se rouvre le même jour si besoin.'
            : 'La classe disparaît du planning de la promotion.'
        }
        ouverte={confirmation !== null}
        onFermer={() => setConfirmation(null)}
        largeur="max-w-md"
      >
        <div className="flex flex-wrap justify-end gap-2 p-4 sm:p-6">
          <button
            type="button"
            onClick={() => setConfirmation(null)}
            className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={gestes.fermer.isPending || gestes.retirer.isPending}
            onClick={() => void (confirmation === 'FERMER' ? fermer() : retirer())}
            className="min-h-[44px] rounded-xl bg-red-600 px-4 text-sm font-semibold text-white disabled:opacity-60"
          >
            {confirmation === 'FERMER' ? 'Fermer pour tous' : 'Retirer'}
          </button>
        </div>
      </Modale>

      <LecteurReplay
        titre={lecture?.titre ?? 'Replay'}
        {...(lecture ? { sousTitre: `${lecture.origineLibelle} · ${dateCourte(lecture.disponibleLe ?? lecture.creeLe)}` } : {})}
        source={lecture ? urlVideoReplay(lecture.id, 'equipe') : null}
        onFermer={() => {
          setLecture(null);
          void gestes.relire();
        }}
      />

      {enSalle && selection && (
        <Suspense fallback={<div className="fixed inset-0 z-[60] !mt-0 bg-codialis-ink" />}>
          <SalleDirect
            titre={selection.libelle}
            sousTitre={`${selection.promotion.libelle} · ${selection.heureDebut}–${selection.heureFin}`}
            role="ANIMATEUR"
            obtenirAcces={() => jetonAnimateur(selection.seanceId)}
            onQuitter={() => {
              setEnSalle(false);
              void gestes.relire();
            }}
            enregistrement={{
              enCours: selection.enregistrementEnCours,
              refusDemarrer: selection.refus.enregistrement,
              refusArreter: selection.refus.arretEnregistrement,
              occupe: gestes.demarrerEnregistrement.isPending || gestes.arreterEnregistrement.isPending,
              onDemarrer: () =>
                void geste(
                  () => gestes.demarrerEnregistrement.mutateAsync(selection.seanceId),
                  'Enregistrement démarré',
                  'L’enregistrement n’a pas pu démarrer.',
                ),
              onArreter: () =>
                void geste(
                  () => gestes.arreterEnregistrement.mutateAsync(selection.seanceId),
                  'Enregistrement arrêté · le replay sera disponible dans quelques minutes',
                  'L’enregistrement n’a pas pu être arrêté.',
                ),
            }}
            onTerminer={async () => {
              await gestes.fermer.mutateAsync(selection.seanceId);
              toast('Classe terminée · la salle est fermée');
            }}
            onRelancer={async (apprenantId) => {
              await geste(
                () => gestes.relancer.mutateAsync({ seanceId: selection.seanceId, apprenantId }),
                'Rappel envoyé dans la salle de l’apprenant',
                'La relance n’a pas pu être envoyée.',
              );
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
